import { useState } from "react";
import { Head, usePage, useForm, router } from "@inertiajs/react";
import classNames from "classnames";
import { toast } from "sonner";
import dayjs from "dayjs";

import DefaultContainer from "@/components/default_container";
import H1 from "@/components/ui/h1";
import H3 from "@/components/ui/h3";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MailIcon } from "lucide-react";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"

export default function MembersIndex({
  current_team,
  current_user,
  members,
  invites,
}) {
  const { permissions } = usePage().props;
  const {
    data: inviteData,
    setData: setInviteData,
    errors: inviteErrors,
    post: invitePost,
    resetAndClearErrors: inviteResetAndClearErrors,
    processing: inviteProcessing
  } = useForm({ email: "", role: "regular" });

  const handleInvite = () => {
    const requestPromise = new Promise((resolve, reject) => {
      invitePost(`/${current_team.slug}/invites`, {
        preserveScroll: true,
        onSuccess: (page) => resolve(page),
        onError: (errors) => {
          reject(errors);

          Object.entries(errors).forEach(([attribute, atErrors]) => {
            toast.error(`${attribute}: ${atErrors.join(", ")}`);
          });
        },
      });
    });

    requestPromise.then(() => inviteResetAndClearErrors());

    toast.promise(requestPromise, {
      loading: "Sending...",
      success: "Invited!",
    });
  };

  return (
    <DefaultContainer>
      <Head title="Team Members" />
      <div>
        <H1>Team members</H1>

        <div className="flex flex-col gap-3 mt-4">
          <MemberLine key={current_user.id} member={members[current_user.id]} current={true} />

          {Object.entries(members).map(([id, member]) => {
            return id !== String(current_user.id) && (<MemberLine key={id} member={member} />)
          })}
        </div>
      </div>

      {permissions.invites.index && (
        <div className="mt-12">
          <H3>Invites</H3>

          <div className="flex flex-col gap-3 mt-4">
            {permissions.invites.create && (
              <div className="pl-6 p-4 bg-gray-50 rounded-md">
                <div className="flex gap-4 items-center">
                  <span className="font-semibold">Invite</span>

                  <InputGroup className="bg-white max-w-64">
                    <InputGroupInput
                      type="email"
                      placeholder="email@example.com"
                      onChange={(e) => setInviteData("email", e.target.value)}
                      value={inviteData.email}
                      disabled={inviteProcessing}
                    />
                    <InputGroupAddon>
                      <MailIcon />
                    </InputGroupAddon>
                  </InputGroup>

                  <RoleSelect
                    id="role-select-invite"
                    onChange={(value) => setInviteData("role", value)}
                    value={inviteData.role}
                    disabled={inviteProcessing}
                  />

                  <Button onClick={handleInvite} disabled={inviteProcessing}>Invite</Button>
                </div>
              </div>
            )}

            {invites && invites.map(invite => (<InviteLine key={invite.id} invite={invite} />))}
          </div>
        </div>
      )}
    </DefaultContainer>
  );
}

function MemberLine({ member, current = false }) {
  const [saving, setSaving] = useState(false);
  const { current_team, roles, permissions } = usePage().props;
  const roleLabel = roles[member.role];

  const { form, data, setData, put, delete: destroy, processing, transform } = useForm({ role: member.role });

  const handleChange = (attribute, value) => {
    setData(attribute, value);

    const requestPromise = new Promise((resolve, reject) => {
      router.put(
        `/${current_team.slug}/members/${member.team_user_id}`,
        { ...data, [attribute]: value },
        {
          onSuccess: (page) => { resolve(page) },
          onError: (errors) => { reject(errors) },
          onStart: () => { setSaving(true) },
          onFinish: () => { setSaving(false) },
          async: true,
          preserveState: true,
        }
      );
    });

    toast.promise(requestPromise, {
      loading: "Saving...",
      success: "Saved!",
      error: "An unexpected error occured",
    });
  };

  const handleDelete = () => {
    const requestPromise = new Promise((resolve, reject) => {
      router.delete(`/${current_team.slug}/members/${member.team_user_id}`, {
        onSuccess: (page) => { resolve(page) },
        onError: (errors) => { reject(errors) },
        onStart: () => { setSaving(true) },
        onFinish: () => { setSaving(false) },
        async: true,
        preserveState: true,
        only: ["members"],
      });
    });

    toast.promise(requestPromise, {
      loading: "Deleting...",
      success: "Deleted!",
      error: "An unexpected error occured",
    });
  };

  const handleLeave = () => {
    const requestPromise = new Promise((resolve, reject) => {
      router.delete(`/${current_team.slug}/members/leave`, {
        onSuccess: (page) => { resolve(page) },
        onError: (errors) => { reject(errors) },
        onStart: () => { setSaving(true) },
        onFinish: () => { setSaving(false) },
        async: true,
      });
    });

    toast.promise(requestPromise, {
      loading: "Leaving...",
      success: "Left team!",
      error: "An unexpected error occured",
    });
  };

  return (
    <div className="flex items-center justify-between pl-6 p-4 bg-gray-50 rounded-md">
      <div className="flex gap-4 items-center">
        <span className={classNames({ "font-semibold": current })}>{member.name}</span>
        {current && (
          <div className="flex gap-1">
            <Badge variant="outline">you</Badge>
            <Badge variant={member.role === "owner" ? "default" : "secondary"}>{roleLabel}</Badge>
          </div>
        )}

        <span className="text-sm">
          {member.email}
        </span>

        {!current && permissions.team_users.update && (
          <RoleSelect id={`role-select-${member.id}`} value={data.role} disabled={saving} onChange={(newValue) => handleChange("role", newValue)} />
        )}
      </div>

      <div className="flex">
        {(current || permissions.team_users.destroy) && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" disabled={saving}>{current ? "Leave" : "Delete"}</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmation</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to {current ? "leave the team" : `delete ${member.name} from the team`}?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={current ? handleLeave : handleDelete}>Continue</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
}

function InviteLine({ invite }) {
  const [processingInvite, setProcessingInvite] = useState(false);
  const createdAt = dayjs(invite.created_at);
  const expiresAt = dayjs(invite.expires_at);
  const isExpired = expiresAt <= dayjs();

  const { current_team, roles, permissions } = usePage().props;
  const roleLabel = roles[invite.role];

  const handleRevoke = () => {
    const requestPromise = new Promise((resolve, reject) => {
      router.delete(`/${current_team.slug}/invites/${invite.id}`, {
        onSuccess: (page) => { resolve(page) },
        onError: (errors) => { reject(errors) },
        onStart: () => { setProcessingInvite(true) },
        onFinish: () => { setProcessingInvite(false) },
        async: true,
        preserveState: true,
        preserveScroll: true,
        only: ["invites"],
      });
    });

    toast.promise(requestPromise, {
      loading: "Revoking...",
      success: "Revoked!",
      error: "An unexpected error occured",
    });
  };

  const handleResend = () => {
    const requestPromise = new Promise((resolve, reject) => {
      router.post(`/${current_team.slug}/invites/${invite.id}/resend`, {}, {
        onSuccess: (page) => { resolve(page) },
        onError: (errors) => { reject(errors) },
        onStart: () => { setProcessingInvite(true) },
        onFinish: () => { setProcessingInvite(false) },
        async: true,
        preserveState: true,
        preserveScroll: true,
        only: ["invites"],
      });
    });

    toast.promise(requestPromise, {
      loading: "Resending...",
      success: "Resent!",
      error: "An unexpected error occured",
    });
  };

  return (
    <div className="flex items-center justify-between pl-6 p-4 bg-gray-50 rounded-md">
      <div className="flex gap-4 items-center">
        <span className="text-muted-foreground">{invite.email}</span>
        <Badge variant={invite.role === "owner" ? "default" : "secondary"}>{roleLabel}</Badge>
        <span className="text-sm">
          Invited {createdAt.format("MMM D, HH:mm")} by {invite.inviter.name}
        </span>
      </div>

      <div className="flex gap-4 items-center">
        <span className={classNames("text-sm", { "text-destructive": isExpired, "text-muted-foreground": !isExpired })}>
          {isExpired ? "Expired" : "Expires"} {expiresAt.format("MMM D, HH:mm")}
        </span>

        {isExpired && permissions.invites.update && (
          <Button variant="outline" onClick={handleResend} disabled={processingInvite}>Resend</Button>
        )}
        {permissions.invites.destroy && (
          <Button variant="destructive" onClick={handleRevoke} disabled={processingInvite}>Revoke</Button>
        )}
      </div>
    </div>
  );
}

function RoleSelect({ id, value = undefined, disabled, onChange }) {
  const { roles } = usePage().props;
  const roleOptions = Object.entries(roles).map(([value, label]) => ({ label, value: String(value) }));
  const convertValue = (value) => (typeof value !== "undefined") ? String(value) : value;

  return (
    <Select
      items={roleOptions}
      onValueChange={onChange}
      value={convertValue(value)}
      name="role"
      id={id}
      disabled={disabled}
    >
      <SelectTrigger className="bg-white">
        <SelectValue />
      </SelectTrigger>

      <SelectContent>
        <SelectGroup>
          {roleOptions.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
